import { useCallback } from 'react';
import { genId } from './genId';

/**
 * Custom hook bundling all the right-click context-menu actions for visuals on
 * the report-builder canvas: z-index changes, delete, duplicate, and "create
 * alert from this visual".
 *
 * Each action mutates the parent's state via the passed-in setters, then closes
 * the context menu. Extracted from ReportBuilderPage.js to keep that file focused
 * on the high-level page composition.
 */
export function useVisualContextMenuActions({
  visuals, setVisuals, activeVisual, setActiveVisual,
  visualResults, allFields,
  visualCtxMenu, setVisualCtxMenu,
  updateVisual, setSavedClean, pushUndo,
  setAlertFromVizData, setAlertFromVizOpen,
}) {
  const getMaxZIndex = useCallback(
    () => Math.max(0, ...visuals.map(v => v.zIndex || 0)),
    [visuals],
  );
  const getMinZIndex = useCallback(
    () => Math.min(0, ...visuals.map(v => v.zIndex || 0)),
    [visuals],
  );

  const handleVisualContextMenu = useCallback((e, vizId) => {
    e.preventDefault();
    setVisualCtxMenu({ x: e.clientX, y: e.clientY, vizId });
  }, [setVisualCtxMenu]);

  const handleZIndexChange = useCallback((action) => {
    if (!visualCtxMenu) return;
    const { vizId } = visualCtxMenu;
    const current = visuals.find(v => v.id === vizId);
    if (!current) { setVisualCtxMenu(null); return; }
    const currentZ = current.zIndex || 0;
    if (action === 'front') updateVisual(vizId, { zIndex: getMaxZIndex() + 1 });
    else if (action === 'back') updateVisual(vizId, { zIndex: getMinZIndex() - 1 });
    else if (action === 'forward') {
      const above = visuals.filter(v => (v.zIndex || 0) > currentZ).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))[0];
      if (above) {
        updateVisual(vizId, { zIndex: above.zIndex || 0 });
        updateVisual(above.id, { zIndex: currentZ });
      } else {
        updateVisual(vizId, { zIndex: currentZ + 1 });
      }
    } else if (action === 'backward') {
      const below = visuals.filter(v => (v.zIndex || 0) < currentZ).sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0))[0];
      if (below) {
        updateVisual(vizId, { zIndex: below.zIndex || 0 });
        updateVisual(below.id, { zIndex: currentZ });
      } else {
        updateVisual(vizId, { zIndex: currentZ - 1 });
      }
    }
    setSavedClean(false);
    setVisualCtxMenu(null);
  }, [visualCtxMenu, visuals, updateVisual, getMaxZIndex, getMinZIndex, setSavedClean, setVisualCtxMenu]);

  const handleDeleteFromCtx = useCallback(() => {
    pushUndo();
    if (!visualCtxMenu) return;
    const remaining = visuals.filter(v => v.id !== visualCtxMenu.vizId);
    setVisuals(remaining);
    if (activeVisual === visualCtxMenu.vizId) setActiveVisual(remaining[0]?.id || '');
    setSavedClean(false);
    setVisualCtxMenu(null);
  }, [visualCtxMenu, visuals, setVisuals, activeVisual, setActiveVisual, setSavedClean, setVisualCtxMenu, pushUndo]);

  const handleDuplicateFromCtx = useCallback(() => {
    if (!visualCtxMenu) return;
    const src = visuals.find(v => v.id === visualCtxMenu.vizId);
    if (!src) { setVisualCtxMenu(null); return; }
    const newId = genId(src.vizType.slice(0, 3) + '_');
    const dup = { ...src, id: newId, title: src.title + ' (Copy)', zIndex: getMaxZIndex() + 1 };
    if (dup.freePosition) {
      dup.freePosition = { ...dup.freePosition, x: (dup.freePosition.x || 50) + 30, y: (dup.freePosition.y || 50) + 30 };
    }
    setVisuals(prev => [...prev, dup]);
    setActiveVisual(newId);
    setSavedClean(false);
    setVisualCtxMenu(null);
  }, [visualCtxMenu, visuals, setVisuals, setActiveVisual, setSavedClean, setVisualCtxMenu, getMaxZIndex]);

  const handleCreateAlertFromViz = useCallback(() => {
    if (!visualCtxMenu) return;
    const viz = visuals.find(v => v.id === visualCtxMenu.vizId);
    if (!viz) { setVisualCtxMenu(null); return; }
    const measures = viz.valueMeasures || [];
    const metric = measures[0] || '';
    const metricLabel = allFields.find(f => f.name === metric)?.description || metric.replace(/_/g, ' ');
    const vr = (visualResults[viz.id] || {}).result;
    const currentVal = vr?.data?.[0]?.[metric];
    const availableMetrics = measures.map(m => ({
      value: m,
      label: allFields.find(f => f.name === m)?.description || m.replace(/_/g, ' '),
    }));
    setAlertFromVizData({
      metric,
      operator: 'gt',
      threshold: currentVal ? String(Math.round(currentVal * 1.1)) : '',
      name: `Alert: ${metricLabel} threshold`,
      frequency: 'daily',
      recipients: '',
      _currentValue: currentVal,
      _metricLabel: metricLabel,
      _vizTitle: viz.title || '',
      _availableMetrics: availableMetrics,
    });
    setAlertFromVizOpen(true);
    setVisualCtxMenu(null);
  }, [visualCtxMenu, visuals, allFields, visualResults, setAlertFromVizData, setAlertFromVizOpen, setVisualCtxMenu]);

  return {
    getMaxZIndex,
    getMinZIndex,
    handleVisualContextMenu,
    handleZIndexChange,
    handleDeleteFromCtx,
    handleDuplicateFromCtx,
    handleCreateAlertFromViz,
  };
}
